import { inject, Injectable, OnDestroy } from '@angular/core';
import { isEqual } from 'lodash-es';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  firstValueFrom,
  map,
  Subject,
} from 'rxjs';
import { EVENTS, IToggle, IVariant, UnleashClient } from 'unleash-proxy-client';
import { DatadogService } from '../datadog';
import { Event, EventBus } from '../rxjs';
import { UnleashEvent, UnleashEventType } from './unleash';
import { UnleashFlags } from './unleash.const';

@Injectable({
  providedIn: 'root',
})
export class UnleashService implements OnDestroy {
  private static instance?: UnleashClient;

  readonly #datadogService = inject(DatadogService);

  readonly #updatedSubject = new BehaviorSubject(0);

  readonly #readySubject = new BehaviorSubject(false);

  readonly #eventBus = new EventBus<UnleashEvent | undefined>();

  #currentToggles: Array<IToggle> = [];

  static init(
    instance: string,
    clientKey: string,
    environment: string,
    refreshInterval: number,
    metricsInterval: number,
    releaseEnvironment: string,
  ) {
    if (!UnleashService.instance) {
      UnleashService.instance = new UnleashClient({
        url: `https://${instance}/api/frontend`,
        environment,
        clientKey,
        appName: 'academy',
        refreshInterval,
        metricsInterval,
        context: {
          properties: {
            releaseEnvironment,
          },
        },
      });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  private get client(): UnleashClient {
    if (!UnleashService.instance) {
      throw new Error('UnleashService not initialized');
    }

    return UnleashService.instance;
  }

  async start() {
    if (this.#readySubject.value) {
      return;
    }

    this.client.on(EVENTS.UPDATE, () => {
      if (isEqual(this.#currentToggles, this.client.getAllToggles())) {
        return;
      }

      this.#currentToggles = this.client.getAllToggles();
      this.#updatedSubject.next(this.#updatedSubject.value + 1);
      this.#eventBus.emit(EVENTS.UPDATE, undefined);
    });

    this.client.on(EVENTS.IMPRESSION, (impression: UnleashEvent) => {
      this.#eventBus.emit(EVENTS.IMPRESSION, impression);
    });

    this.client.on(EVENTS.READY, () => {
      this.#eventBus.emit(EVENTS.READY, undefined);
      this.#readySubject.next(true);
    });

    this.client.on(EVENTS.INIT, () => {
      this.#eventBus.emit(EVENTS.INIT, undefined);
    });

    await this.client.start();
  }

  ngOnDestroy() {
    if (!this.#readySubject.value) {
      return;
    }

    this.client.off('update');
    this.#updatedSubject.complete();
    this.client.stop();
  }

  isEnabled(feature: UnleashFlags) {
    const isEnabled = this.client.isEnabled(feature);
    this.#datadogService.addFeatureFlag(feature, isEnabled);

    return isEnabled;
  }

  isDisabled(feature: UnleashFlags) {
    return !this.isEnabled(feature);
  }

  isEnabled$(feature: UnleashFlags) {
    return combineLatest([this.#readySubject, this.#updatedSubject]).pipe(
      filter(([ready]) => ready),
      map(() => this.isEnabled(feature)),
    );
  }

  isDisabled$(feature: UnleashFlags) {
    return combineLatest([this.#readySubject, this.#updatedSubject]).pipe(
      filter(([ready]) => ready),
      map(() => this.isDisabled(feature)),
    );
  }

  isEnabledVariant(feature: UnleashFlags): IVariant | false {
    const variant = this.client.getVariant(feature);
    this.#datadogService.addFeatureFlag(
      feature,
      variant.feature_enabled === true,
    );

    return variant.feature_enabled === true ? variant : false;
  }

  isEnabledVariant$(feature: UnleashFlags) {
    return this.#updatedSubject.pipe(map(() => this.isEnabledVariant(feature)));
  }

  getVariantName(feature: UnleashFlags): string {
    const variant = this.isEnabledVariant(feature);
    return variant ? variant.name : 'Control';
  }

  getVariantName$(feature: UnleashFlags) {
    return this.#updatedSubject.pipe(map(() => this.getVariantName(feature)));
  }

  watch() {
    return this.#updatedSubject.asObservable();
  }

  #debounceProperties = new BehaviorSubject<Record<string, string> | undefined>(
    undefined,
  );

  #contextUpdated = new Subject<void>();

  constructor() {
    this.#debounceProperties
      .pipe(
        filter((properties) => !!properties),
        debounceTime(1000),
      )
      .subscribe(async (properties) => {
        if (
          JSON.stringify(properties) !==
          JSON.stringify(this.client.getContext().properties ?? {})
        ) {
          await this.client.updateContext({ properties });
        }

        this.#debounceProperties.next(undefined);
        this.#contextUpdated.next();
      });
  }

  async addContext(context: { [field: string]: string | undefined }) {
    const properties = this.#debounceProperties.value || {
      ...this.client.getContext().properties,
    }; // spread to avoid mutating the original object;

    Object.entries(context).forEach(([key, value]) => {
      if (properties[key] && value === undefined) {
        delete properties[key];
      } else if (value && (!properties[key] || properties[key] !== value)) {
        properties[key] = value;
      }
    });

    if (
      Object.keys(properties).length === 0 &&
      Object.keys(context).length === 0
    ) {
      return;
    }

    this.#debounceProperties.next(properties);

    await firstValueFrom(this.#contextUpdated);
  }

  setContext(context: string, value: string) {
    this.client.setContextField(context, value);
  }

  unsetContext(...context: Array<string>) {
    this.addContext(
      context.reduce((acc, key) => ({ ...acc, [key]: undefined }), {}),
    );
  }

  on(
    key: UnleashEventType,
    callback: (data: Event<UnleashEvent | undefined>) => void,
  ) {
    return this.#eventBus.on(key, callback);
  }
}
